import type { AnalyticsEvent } from '@faststore/sdk'
import { useAnalyticsEvent } from '@faststore/sdk'

import storeConfig from '../../../store.config'
import { trackDynamicYieldAnalyticsEvent } from './dynamicYield'
import type {
  AutocompleteSuggestionClickedEvent,
  AutocompleteSuggestionsShownEvent,
  FooterClick,
  GTMUserEvent,
  KitParams,
  MenuCategory,
  NotifyMeSignupEvent,
  PromoClickEvent,
  PromoImpressionsEvent,
  RecurringOrderEvent,
  RecurringOrderFrequencyEvent,
  ScrollDepth,
  SearchPreviewClickEvent,
  SearchPreviewEvent,
} from './types'
import { isAnalyticsEvent } from './utils'

if (typeof window !== 'undefined') {
  window.dataLayer = window.dataLayer ?? []
}

type ExtendedEvent =
  | GTMUserEvent
  | AnalyticsEvent
  | SearchPreviewEvent
  | SearchPreviewClickEvent
  | RecurringOrderFrequencyEvent
  | RecurringOrderEvent
  | ScrollDepth
  | MenuCategory
  | FooterClick
  | PromoImpressionsEvent
  | PromoClickEvent
  | AutocompleteSuggestionsShownEvent
  | AutocompleteSuggestionClickedEvent
  | NotifyMeSignupEvent

const getKitParams = (source: unknown) => {
  const s = (source ?? {}) as KitParams

  const isKit =
    s.bundle_label &&
    (s.bundle_discount || s.bundle_discount === 0) &&
    (s.bundle_discount_percentage || s.bundle_discount_percentage === 0)

  return isKit
    ? {
        bundle_label: s.bundle_label,
        bundle_discount: s.bundle_discount,
        bundle_discount_percentage: s.bundle_discount_percentage,
      }
    : {}
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const handleCartParse = (item: any) => {
  const isKit =
    item.bundle_label &&
    (item.bundle_discount || item.bundle_discount === 0) &&
    (item.bundle_discount_percentage || item.bundle_discount_percentage === 0)

  const unitMultiplier = item?.unitMultiplier ?? 1

  return {
    object_id: item.object_id,
    notify_channel: item.notify_channel,
    position: item.position,
    item_id: item.item_id,
    item_name: item.item_name,
    coupon: item.coupon,
    currency: item.currency,
    discount: item.discount,
    index: item.index,
    item_brand: item.item_brand,
    item_category: item.item_category,
    item_list_id: item.item_list_id,
    item_list_name: item.item_list_name,
    item_variant: item.item_variant,
    price: item.price / unitMultiplier,
    quantity: item.quantity,
    unitMultiplier,
    full_price: item.full_price / unitMultiplier,
    discount_percentage: item.discount_percentage,
    item_variant_name: item.item_variant_name,
    item_reference_id: item.product_reference_id,
    // Adding optional kit parameters
    ...(isKit && {
      bundle_label: item.bundle_label,
      bundle_discount: item.bundle_discount,
      bundle_discount_percentage: item.bundle_discount_percentage,
    }),
  }
}

const parseToUniversalAnalyticsData = (evt: ExtendedEvent) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  switch (evt.name) {
    case 'view_item': {
      return {
        event: 'view_item',
        ecommerce: {
          ...evt.params,
          // Adding optional kit parameters
          ...getKitParams(evt.params),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          items: (evt.params.items ?? []).map((item: any) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            let result: any = ''

            result = handleCartParse(item)
            result.availability = item.availability

            return result
          }),
        },
      }
    }

    case 'notify_me_signup': {
      return {
        event: 'notify_me_signup',
        ecommerce: {
          ...evt.params,
          // Adding optional kit parameters
          ...getKitParams(evt.params),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          items: (evt.params.items ?? []).map((item: any) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            let result: any = ''

            result = handleCartParse(item)
            result.availability = item.availability

            return result
          }),
        },
      }
    }

    case 'select_item': {
      return {
        event: 'select_item',
        ecommerce: {
          ...evt.params,
          // Adding optional kit parameters
          ...getKitParams(evt.params),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          items: (evt.params.items ?? []).map((item: any) => {
            let result: any = ''

            result = handleCartParse(item)

            result.search_term = item.search_term

            return result
          }),
        },
      }
    }

    case 'add_to_cart': {
      return {
        event: 'add_to_cart',
        ecommerce: {
          ...evt.params,
          orderFormId: (evt.params as any).orderFormId ?? '',
          // Adding optional kit parameters
          ...getKitParams(evt.params),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          items: (evt.params.items ?? []).map((item: any) => {
            let result: any = ''

            result = handleCartParse(item)
            result.box_type = item.box_type
            result.box_quantity = item.quantity
            result.box_discount = item.box_discount

            return result
          }),
        },
      }
    }

    case 'remove_from_cart': {
      return {
        event: 'remove_from_cart',
        ecommerce: {
          orderFormId: (evt.params as any).orderFormId ?? '',
          // Adding optional kit parameters
          ...getKitParams(evt.params),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          items: (evt.params.items ?? []).map((item) => handleCartParse(item)),
        },
      }
    }

    case 'view_item_list': {
      return {
        event: 'view_item_list',
        ecommerce: {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          ...evt.params,
          items: (evt.params.items ?? []).map((item) => handleCartParse(item)),
        },
      }
    }

    case 'begin_checkout': {
      return {
        event: 'begin_checkout',
        ecommerce: {
          orderFormId: (evt.params as any).orderFormId ?? '',
          // Adding optional kit parameters
          currency: evt.params.currency,
          value: evt.params.value,
          ...evt.params,
          ...getKitParams(evt.params),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          items: (evt.params.items ?? []).map((item) => handleCartParse(item)),
        },
      }
    }

    case 'add_to_wishlist': {
      return {
        event: 'add_to_wishlist',
        ecommerce: {
          currency: evt.params.currency,
          value: evt.params.value,
          ...evt.params,
          // Adding optional kit parameters
          ...getKitParams(evt.params),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          items: (evt.params.items ?? []).map((item) => handleCartParse(item)),
        },
      }
    }

    case 'view_cart': {
      return {
        event: 'view_cart',
        ecommerce: {
          orderFormId: (evt.params as any).orderFormId ?? '',
          // Adding optional kit parameters
          currency: evt.params.currency,
          value: evt.params.value,
          ...getKitParams(evt.params),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          items: (evt.params.items ?? []).map((item) => handleCartParse(item)),
        },
      }
    }

    case 'select_promotion':
      return {
        event: 'select_promotion',
        ecommerce: {
          items: [
            {
              promotion_id: evt.params.promotion_id ?? '',
              promotion_name: evt.params.promotion_name ?? '',
              creative_name: evt.params.creative_name ?? '',
              creative_slot: evt.params.creative_slot ?? '',
            },
          ],
        },
      }

    case 'view_promotion':
      return {
        event: 'view_promotion',
        ecommerce: {
          items: [
            {
              promotion_id: evt.params.promotion_id ?? '',
              promotion_name: evt.params.promotion_name ?? '',
              creative_name: evt.params.creative_name ?? '',
              creative_slot: evt.params.creative_slot ?? '',
            },
          ],
        },
      }

    case 'userData':
      return {
        event: 'userData',
        loginStatus: evt.params.loginStatus,
        userID: evt.params.userID,
        ...(evt.params.bk_account_id && {
          bk_account_id: evt.params.bk_account_id,
        }),
        ...(evt.params.vtex_user_id && {
          vtex_user_id: evt.params.vtex_user_id,
        }),
        ...(evt.params.emailHash && {
          emailHash: evt.params.emailHash,
        }),
        visitorType: evt.params.visitorType,
        visitorContactInfo: {
          hashed_email: evt.params.visitorContactInfo.hashed_email,
        },
        visitorOptinNewsLetter: evt.params.visitorOptinNewsLetter,
        pageType: evt.params.pageType,
      }

    case 'search_preview_event':
      return {
        ...evt.params,
      }

    case 'search_suggestion_shown':
      return {
        ...evt.params,
      }

    case 'search_suggestion_clicked':
      return {
        ...evt.params,
      }

    case 'search':
      return {
        ...evt.params,
      }

    case 'search_preview_click_event':
      return {
        event: 'ga4_event',
        event_name: evt.params.event_name,
        clickArea: evt.params.clickArea,
        clicked_result: evt.params.clicked_result,
        search_term: evt.params.search_term,
      }

    case 'recurring_order_frequency_event':
      return {
        event: 'ga4_event',
        event_name: evt.params.event_name,
        frequency: evt.params.frequency,
      }

    case 'recurring_order_event':
      return {
        event: 'ga4_event',
        event_name: evt.params.event_name,
      }

    case 'scroll_depth':
      return {
        event: 'ga4_event',
        event_name: 'scroll_depth',
        scroll_percentage: evt.params.scroll_threshold + '%',
      }

    case 'menu_categorie':
      return {
        event: 'ga4_event',
        event_name: 'header',
        section: evt.params.section,
        main_category: evt.params.main_category,
        category_level: evt.params.path,
      }

    case 'footer':
      return {
        event: 'ga4_event',
        event_name: 'footer',
        click_area: evt.params.click_area,
        link_clicked: evt.params.link_clicked,
      }

    default: {
      return null
    }
  }
}

const removeInternalAnalyticsParams = (event: ExtendedEvent): ExtendedEvent => {
  if (!('params' in event) || !event.params) {
    return event
  }

  const params = { ...(event.params as Record<string, unknown>) }

  delete params.dynamicYield

  return {
    ...event,
    params,
  } as ExtendedEvent
}

export const AnalyticsHandler = () => {
  useAnalyticsEvent((event: ExtendedEvent) => {
    // dynamicYield params are internal and must not reach GA/dataLayer
    const universalAnalyticsData = parseToUniversalAnalyticsData(
      removeInternalAnalyticsParams(event)
    )

    // Cleans the ecommerce object before pushing a new one
    // This prevents the new data from getting merged with the previous one
    // which could lead do inaccurate and old data being sent with events
    //
    // source: https://developers.google.com/tag-manager/ecommerce-ga4?hl=pt-br#clearing_the_ecommerce_object
    if (isAnalyticsEvent(event?.name)) {
      window.dataLayer.push({ ecommerce: null })
    }

    window.dataLayer.push(universalAnalyticsData)

    trackDynamicYieldAnalyticsEvent(event)

    const platformEvent = removeInternalAnalyticsParams(event)

    import(`./platform/${storeConfig.platform}`).then(
      ({ default: sendEvent }) => {
        sendEvent(platformEvent)
      }
    )
  })

  return null
}

export default AnalyticsHandler
